import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useMemo } from 'react';
import { listRecentSearches, upsertRecentSearch, deleteRecentSearch, clearRecentSearches, RecentSearchDto, UpsertRecentSearchPayload } from 'src/api/recentSearchApi';
import { useAuth } from 'src/contexts/AuthContext';
import {
   LocalRecentSearch,
   clearLocalRecentSearches,
   readLocalRecentSearches,
   removeLocalRecentSearchByMapboxId,
   upsertLocalRecentSearch,
} from 'src/utils/recentSearchStorage';
import { RECENT_SEARCHES_DEFAULT_LIMIT } from 'src/utils/constants';

export const RecentSearchQueryKeys = {
   List: 'recentSearches.list',
};

export type RecentSearchItem = {
   id?: string;
   mapboxId: string;
   placeName: string;
   center: string;
   mapboxObject?: any;
   checkin: string;
   checkout: string;
   adults?: number;
   children?: number;
   rooms?: number;
   yearOlds?: number[] | null;
   isPet?: boolean;
   countryCode?: string | null;
   organizationId?: string | null;
   lastSearchedAt: string;
};

const today = () => moment().startOf('day').format('YYYY-MM-DD');

const fromApi = (row: RecentSearchDto): RecentSearchItem => ({
   id: row.id,
   mapboxId: row.mapboxId,
   placeName: row.placeName,
   center: row.center,
   mapboxObject: row.mapboxObject,
   checkin: row.checkin,
   checkout: row.checkout,
   adults: row.adults,
   children: row.children,
   rooms: row.rooms,
   yearOlds: row.yearOlds ?? undefined,
   isPet: row.isPet,
   countryCode: row.countryCode ?? undefined,
   organizationId: row.organizationId ?? undefined,
   lastSearchedAt: row.lastSearchedAt,
});

const fromLocal = (row: LocalRecentSearch): RecentSearchItem => ({
   mapboxId: row.mapboxId,
   placeName: row.placeName,
   center: row.center,
   mapboxObject: row.mapboxObject,
   checkin: row.checkin,
   checkout: row.checkout,
   adults: row.adults,
   children: row.children,
   rooms: row.rooms,
   yearOlds: row.yearOlds,
   isPet: row.isPet,
   countryCode: row.countryCode,
   organizationId: row.organizationId,
   lastSearchedAt: row.lastSearchedAt,
});

export const useRecentSearches = ({ limit = RECENT_SEARCHES_DEFAULT_LIMIT }: { limit?: number } = {}) => {
   const { isAuthenticated } = useAuth();

   const query = useQuery({
      queryKey: [RecentSearchQueryKeys.List, limit, isAuthenticated],
      queryFn: async (): Promise<RecentSearchItem[]> => {
         if (isAuthenticated) {
            const res = await listRecentSearches(limit);
            const cutoff = today();
            return ((res?.data ?? []) as RecentSearchDto[])
               .filter(r => r.checkin >= cutoff)
               .slice(0, limit)
               .map(fromApi);
         }
         return readLocalRecentSearches().slice(0, limit).map(fromLocal);
      },
      staleTime: isAuthenticated ? 30_000 : 0,
   });

   const data = useMemo<RecentSearchItem[]>(() => query.data ?? [], [query.data]);

   return {
      data,
      isLoading: query.isLoading,
   };
};

export const useUpsertRecentSearch = () => {
   const { isAuthenticated } = useAuth();
   const queryClient = useQueryClient();

   const mutation = useMutation({
      mutationFn: async (payload: UpsertRecentSearchPayload) => {
         if (isAuthenticated) {
            const res = await upsertRecentSearch(payload);
            return res?.data;
         }
         upsertLocalRecentSearch({
            ...payload,
            lastSearchedAt: new Date().toISOString(),
         });
         return null;
      },
      onSuccess: () => {
         queryClient.invalidateQueries({ queryKey: [RecentSearchQueryKeys.List] });
      },
   });

   return mutation;
};

export const useDeleteRecentSearch = () => {
   const { isAuthenticated } = useAuth();
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async ({ id, mapboxId }: { id?: string; mapboxId?: string }) => {
         if (isAuthenticated && id) {
            await deleteRecentSearch(id);
            return;
         }
         // Anonymous — or a row that never got a server id — comes from the
         // local store.
         if (mapboxId) {
            removeLocalRecentSearchByMapboxId(mapboxId);
         }
      },
      // Drop the chip from every cached list immediately; roll back if the
      // server delete fails so a silent failure can't strand a stale chip.
      onMutate: async ({ id, mapboxId }) => {
         await queryClient.cancelQueries({ queryKey: [RecentSearchQueryKeys.List] });
         const previous = queryClient.getQueriesData<RecentSearchItem[]>({ queryKey: [RecentSearchQueryKeys.List] });
         queryClient.setQueriesData<RecentSearchItem[]>({ queryKey: [RecentSearchQueryKeys.List] }, old =>
            (old ?? []).filter(item => {
               if (id) return item.id !== id;
               if (mapboxId) return item.mapboxId !== mapboxId;
               return true;
            }),
         );
         return { previous };
      },
      onError: (_err, _vars, context) => {
         context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      },
      onSettled: () => {
         queryClient.invalidateQueries({ queryKey: [RecentSearchQueryKeys.List] });
      },
   });
};

export const useClearRecentSearches = () => {
   const { isAuthenticated } = useAuth();
   const queryClient = useQueryClient();

   return useMutation({
      mutationFn: async () => {
         if (isAuthenticated) {
            await clearRecentSearches();
            return;
         }
         clearLocalRecentSearches();
      },
      // Empty the cached lists immediately; roll back if the server call fails.
      onMutate: async () => {
         await queryClient.cancelQueries({ queryKey: [RecentSearchQueryKeys.List] });
         const previous = queryClient.getQueriesData<RecentSearchItem[]>({ queryKey: [RecentSearchQueryKeys.List] });
         queryClient.setQueriesData<RecentSearchItem[]>({ queryKey: [RecentSearchQueryKeys.List] }, () => []);
         return { previous };
      },
      onError: (_err, _vars, context) => {
         context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data));
      },
      onSettled: () => {
         queryClient.invalidateQueries({ queryKey: [RecentSearchQueryKeys.List] });
      },
   });
};
